import { ReactSetStateFunction } from "@custom-types/types";
import { useEffect, useState } from "react";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";

interface UseCheckWhatsNew {
  /** Indicates whether there are new articles available in the "What's New" section */
  newArticle: string | undefined;

  /** Function to manually set the new article state */
  setNewArticle: ReactSetStateFunction<string | undefined>;
}

/** Key used to store the latest article identifier in local storage */
export const LATEST_WHATS_NEW_ARTICLE_KEY = "latestWhatsNewArticle";
/**
 * Hook to check for new articles in the "What's New" section.
 * It fetch the base64 encoded URL of the latest article and compares it with a stored value in local storage.
 * If they differ or value is not available in local storage, it indicates that there are new articles available.
 *
 * @returns An object containing the latest article identifier and a function to update it.
 */
export function useCheckWhatsNew(): UseCheckWhatsNew {
  const [newArticle, setNewArticle] = useState<string | undefined>(undefined);

  const { stellarApiClient } = useStellarApiClient();

  // Fetch base64 encoded url of the latest article
  useEffect(() => {
    async function fetchLatestArticle(): Promise<void> {
      try {
        const response = await stellarApiClient.productUpdate.getNewUpdate();

        if (!response.latestArticle) {
          return;
        }

        const latestArticle = response.latestArticle;
        const storedArticle = localStorage.getItem(
          LATEST_WHATS_NEW_ARTICLE_KEY
        );

        if (storedArticle !== latestArticle) {
          setNewArticle(latestArticle);
        }
      } catch (_) {
        // Skip error silently
      }
    }

    void fetchLatestArticle();
  }, [stellarApiClient.productUpdate]);

  return {
    newArticle,
    setNewArticle,
  };
}
